import { useEffect, useState } from 'react';
import {
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Typography,
  makeStyles,
} from '@material-ui/core';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import { useApi } from '@backstage/core-plugin-api';
import { spassuCodeApiRef } from '../../api';
import { AzureWorkItemDetail } from '../../api/types';

const useStyles = makeStyles(theme => ({
  root: {
    borderTop: `3px solid ${theme.palette.secondary.main}`,
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  headerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
  typeChip: {
    fontWeight: 600,
    fontSize: '0.7rem',
    height: 22,
  },
  stateChip: {
    fontSize: '0.7rem',
    height: 22,
  },
  meta: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5),
    marginBottom: theme.spacing(1.5),
  },
  section: {
    marginTop: theme.spacing(1.5),
  },
  sectionTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(0.5),
  },
  content: {
    whiteSpace: 'pre-wrap',
    fontSize: '0.85rem',
    lineHeight: 1.5,
    maxHeight: 200,
    overflow: 'auto',
    padding: theme.spacing(1),
    backgroundColor: theme.palette.background.default,
    borderRadius: theme.shape.borderRadius,
  },
  tags: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(1),
  },
  tagChip: {
    fontSize: '0.65rem',
    height: 18,
  },
  parent: {
    marginTop: theme.spacing(1),
    fontSize: '0.8rem',
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(3),
  },
  error: {
    padding: theme.spacing(2),
    textAlign: 'center',
  },
}));

export interface WorkItemPreviewProps {
  projectId: string;
  workItemId: number;
}

export const WorkItemPreview = ({
  projectId,
  workItemId,
}: WorkItemPreviewProps) => {
  const classes = useStyles();
  const api = useApi(spassuCodeApiRef);
  const [detail, setDetail] = useState<AzureWorkItemDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    api.getWorkItem(projectId, workItemId).then(data => {
      if (!cancelled) {
        setDetail(data);
        setLoading(false);
      }
    }).catch(err => {
      if (!cancelled) {
        const msg = err instanceof Error ? err.message : String(err);
        let friendly: string;
        if (msg === 'WORK_ITEM_NOT_FOUND') {
          friendly = 'Work Item não encontrado.';
        } else if (msg === 'AZURE_PERMISSION_DENIED') {
          friendly = 'Permissão negada para acessar este Work Item.';
        } else {
          friendly = `Erro ao carregar: ${msg}`;
        }
        setError(friendly);
        setLoading(false);
      }
    });

    return () => { cancelled = true; };
  }, [api, projectId, workItemId]);

  if (loading) {
    return (
      <div className={classes.loading}>
        <CircularProgress size={24} />
      </div>
    );
  }

  if (error || !detail) {
    return (
      <div className={classes.error}>
        <Typography color="error" variant="body2">
          {error ?? 'Work Item não encontrado.'}
        </Typography>
      </div>
    );
  }

  return (
    <div className={classes.root}>
      {/* Header */}
      <div className={classes.header}>
        <div>
          <Typography variant="subtitle1" component="h3">
            #{detail.id} — {detail.title}
          </Typography>
        </div>
        <div className={classes.headerActions}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<OpenInNewIcon />}
            href={detail.url}
            target="_blank"
            rel="noopener noreferrer"
          >
            Azure Boards
          </Button>
          <IconButton
            size="small"
            aria-label={expanded ? 'Colapsar' : 'Expandir'}
            onClick={() => setExpanded(prev => !prev)}
          >
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </div>
      </div>

      {/* Meta chips */}
      <div className={classes.meta}>
        <Chip label={detail.type} className={classes.typeChip} color="primary" size="small" />
        <Chip label={detail.state} className={classes.stateChip} variant="outlined" size="small" />
        {detail.assignedTo && (
          <Chip label={detail.assignedTo} className={classes.stateChip} variant="outlined" size="small" />
        )}
      </div>

      {expanded && (
        <>
          <Divider />

          {/* Description */}
          {detail.descriptionText && (
            <div className={classes.section}>
              <Typography variant="body2" className={classes.sectionTitle}>
                Descrição
              </Typography>
              <div className={classes.content}>{detail.descriptionText}</div>
            </div>
          )}

          {/* Acceptance Criteria */}
          {detail.acceptanceCriteriaText && (
            <div className={classes.section}>
              <Typography variant="body2" className={classes.sectionTitle}>
                Critérios de Aceitação
              </Typography>
              <div className={classes.content}>{detail.acceptanceCriteriaText}</div>
            </div>
          )}

          {/* Repro Steps */}
          {detail.reproStepsText && (
            <div className={classes.section}>
              <Typography variant="body2" className={classes.sectionTitle}>
                Passos para Reproduzir
              </Typography>
              <div className={classes.content}>{detail.reproStepsText}</div>
            </div>
          )}

          {/* Tags */}
          {detail.tags && detail.tags.length > 0 && (
            <div className={classes.tags}>
              {detail.tags.map(tag => (
                <Chip key={tag} label={tag} className={classes.tagChip} variant="outlined" size="small" />
              ))}
            </div>
          )}

          {/* Parent */}
          {detail.parent && (
            <Typography variant="body2" color="textSecondary" className={classes.parent}>
              Parent: #{detail.parent.id} {detail.parent.type} — {detail.parent.title}
            </Typography>
          )}
        </>
      )}
    </div>
  );
};
